import { Injectable } from '@angular/core';
import {
	AbstractControl,
	FormControl,
	ValidationErrors,
	ValidatorFn,
	Validators
} from '@angular/forms';
import {NzSafeAny} from 'ng-zorro-antd/core/types';
import {Observable, Observer} from 'rxjs';
import {JSONSchema7} from 'json-schema';

export type MyErrorsOptions = { 'de': string; en: string } & Record<string, NzSafeAny>;
export type MyValidationErrors = Record<string, MyErrorsOptions>;

@Injectable({
	providedIn: 'root'
})
export class FormValidatorService extends Validators {
	static minLength(minLength: number): ValidatorFn {
		return (control: AbstractControl): MyValidationErrors | null => {
			if (Validators.minLength(minLength)(control) === null) {
				return null;
			}
			return { minlength: { de: `Mindestlänge ist ${minLength}`, en: `MinLength is ${minLength}` } };
		};
	}

	static maxLength(maxLength: number): ValidatorFn {
		return (control: AbstractControl): MyValidationErrors | null => {
			if (Validators.maxLength(maxLength)(control) === null) {
				return null;
			}
			return { maxlength: { de: `Maximallänge ist ${maxLength}`, en: `MaxLength is ${maxLength}` } };
		};
	}

	static minItems(minItems: number): ValidatorFn {
		return (control: AbstractControl): MyValidationErrors | null => {
			if (control?.value?.length >= minItems) {
				return null;
			}
			return { minitems: { de: `Mindestens ist ${minItems} Selektionen`, en: `At least ${minItems} selections` } };
		};
	}

	static maxItems(maxItems: number): ValidatorFn {
		return (control: AbstractControl): MyValidationErrors | null => {
			if (control?.value?.length <= maxItems) {
				return null;
			}
			return { maxitems: { de: `Maximal ${maxItems} Selektionen`, en: `Not more than ${maxItems} selections` } };
		};
	}

	static mobile(control: AbstractControl): MyValidationErrors | null {
		const value = control.value;

		if (this.isEmptyInputValue(value)) {
			return null;
		}

		return this.isMobile(value) ? null : { mobile: {
			de: `Die Telefonnummer ist nicht valide`,
			en: `Mobile phone number is not valid`,
		}};
	}

	//FIXME GSP-255 User Erstellen - Formularvalidierung
	static userNameAsyncValidator = (control: AbstractControl) =>
		new Observable((observer: Observer<MyValidationErrors | null>) => {
			setTimeout(() => {
				if (control.value === 'JasonWood') {
					observer.next({
						duplicated: { de: `Der Benutzername ist redundant`, en: `The username is redundant!` }
					});
				} else {
					observer.next(null);
				}
				observer.complete();
			}, 1000);
		});

	//FIXME GSP-255 User Erstellen - Formularvalidierung
	private static userNameValidator(): ValidatorFn {
		return (control: AbstractControl): MyValidationErrors | null => {
			// TODO GSP-255 -> Async-ValidatorFn überhaupt möglich?
			// const status = await this.userNameAsyncValidator(control).toPromise();
			return null;
		};
	}

	//FIXME GSP-255 User Erstellen - Formularvalidierung
	private static password(): ValidatorFn {
		const passwordConfirm = document.getElementById('passwordConfirm')?.innerText;
		return (control: AbstractControl): MyValidationErrors | null => {
			console.log(passwordConfirm);
			if (control?.value?.toString() === passwordConfirm) {
				return null;
			}
			return { password: { de: `Passwörter stimmen nicht überein.`, en: `Passwords do not match.` } };
		};
	}

	private static isEmptyInputValue(value: NzSafeAny): boolean {
		return value == null || value.length === 0;
	}

	private static isMobile(value: string): boolean {
		return typeof value === 'string' && /(^1\d{10}$)/.test(value);
	}

	//FIXME GSP-255 User Erstellen - Formularvalidierung
	static getValidatorBySchema(schema: JSONSchema7): ValidationErrors[] | null {
		const validationErrors = [];
		if (!schema) {
			return null;
		}

		if (schema.format === 'email') {
			validationErrors.push(FormValidatorService.email);
		}

		if (schema.format === 'password') {
			validationErrors.push(FormValidatorService.password);
		}

		if (schema.title?.toLowerCase().includes('username') || schema.$id?.toLowerCase().includes('username')) {
			validationErrors.push(FormValidatorService.userNameValidator());
		}

		if (schema.minLength > 0) {
			validationErrors.push(FormValidatorService.minLength(schema.minLength));
		}
		if (schema.maxLength > 0) {
			validationErrors.push(FormValidatorService.maxLength(schema.maxLength));
		}

		if (schema.minItems > 0) {
			validationErrors.push(FormValidatorService.minItems(schema.minItems));
		}
		if (schema.maxItems > 0) {
			validationErrors.push(FormValidatorService.maxItems(schema.maxItems));
		}

		return validationErrors;
	}
}
